import {
    BabyIcon,
    BadgePercent,
    BaggageClaim,
    BarChart3,
    BookMarked,
    BookOpen,
    BookUser,
    Brush,
    ChefHat,
    Clock,
    Coins,
    FileText,
    Gift,
    HandHeart,
    History,
    Import,
    Landmark,
    LayoutDashboard,
    Lock,
    Map,
    Megaphone,
    MessagesSquare,
    Monitor,
    MoreHorizontal,
    NewspaperIcon,
    PiggyBank,
    Puzzle,
    QrCode,
    Rocket,
    Search,
    Settings2,
    ShoppingBag,
    ShoppingBasket,
    ShoppingCart,
    SquarePercent,
    Store,
    Truck,
    Users,
    Users2,
    Utensils,
    UtensilsCrossed,
} from "lucide-react";
import { t } from "@lingui/core/macro";

import { Routes } from "#navigation/routes";
import { getWebsiteSetupUrl } from "#shared-components/setup-form-urls";

import { RouteItem, RouteOnClick, StorePermission } from "./types";

// Store slug state management
export let currentStoreSlug = "";
export const setStoreSlug = (slug: string) => {
    currentStoreSlug = slug;
};

// See README.md for detailed documentation on route items and navigation

export const INTERNAL_ROUTES = [
    Routes.SnackpassAdmin,
    Routes.InternalSettings,
    Routes.InternalFees,
    Routes.InternalSeniorSupport,
    Routes.InternalDevices,
];

export const getRouteItems = (): Record<
    Exclude<Routes, Routes.Marketplace>,
    RouteItem
> => ({
    // Quick Actions
    [Routes.Onboarding]: {
        title: t`Onboarding`,
        url: Routes.Onboarding,
        icon: BabyIcon,
        permission: StorePermission.HasDashboard,
    },
    [Routes.WhatsNew]: {
        title: t`What's New`,
        url: Routes.WhatsNew,
        icon: NewspaperIcon,
        permission: StorePermission.HasDashboard,
    },
    [Routes.Search]: {
        url: Routes.Search,
        title: t`Search`,
        icon: Search,
        onClick: RouteOnClick.GlobalSearch,
    },

    // Store/Platform
    [Routes.Index]: {
        title: t`Dashboard`,
        url: Routes.Index,
        icon: LayoutDashboard,
        searchKeywords: ["net sales", "ticket size", "dashboard"],
        permission: StorePermission.HasDashboard,
    },
    [Routes.BetaOrders]: {
        title: t`Orders`,
        url: Routes.BetaOrders,
        icon: ShoppingCart,
        searchKeywords: ["order summary", "order history"],
        permission: StorePermission.HasOrders,
        nonSidebarSubpages: [Routes.OrdersDetails, Routes.PurchaseDetails],
    },
    [Routes.OrdersDetails]: {
        title: t`Order Details`,
        url: Routes.OrdersDetails,
        permission: StorePermission.HasOrders,
    },
    [Routes.PurchaseDetails]: {
        title: t`Purchase Details`,
        url: Routes.PurchaseDetails,
        permission: StorePermission.HasOrders,
    },

    // Menu
    [Routes.Menu]: {
        url: Routes.Menu,
        title: t`Menu`,
        icon: BookOpen,
        searchKeywords: ["menu", "menu editor", "menu items"],
        permission: StorePermission.HasMenuEditing,
        isNavigationOnly: true,
    },
    [Routes.MenuEditor]: {
        title: t`Items`,
        url: Routes.MenuEditor,
        searchKeywords: ["new item", "menu edit", "menu editor"],
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.MenuManager]: {
        title: t`Menus`,
        url: Routes.MenuManager,
        searchKeywords: ["special menus", "menu manager"],
        permission: StorePermission.HasMenuEditing,
        nonSidebarSubpages: [Routes.NEW_MENU, Routes.EDIT_MENU],
    },
    [Routes.NEW_MENU]: {
        title: t`New Menu`,
        url: Routes.NEW_MENU,
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.EDIT_MENU]: {
        title: t`Edit Menu`,
        url: Routes.EDIT_MENU,
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.MenuManagerBeta]: {
        title: t`Menu Manager`,
        url: Routes.MenuManagerBeta,
        permission: StorePermission.HasMenuEditing,
        isBeta: true,
        nonSidebarSubpages: [
            Routes.MenuManagerBetaCategories,
            Routes.MenuManagerBetaItems,
            Routes.MenuManagerBetaModifierGroups,
        ],
    },
    [Routes.MenuManagerBetaCategories]: {
        title: t`Categories`,
        url: Routes.MenuManagerBetaCategories,
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.MenuManagerBetaItems]: {
        title: t`Categories`,
        url: Routes.MenuManagerBetaItems,
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.MenuManagerBetaModifierGroups]: {
        title: t`Categories`,
        url: Routes.MenuManagerBetaModifierGroups,
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.MenuInventoryManager]: {
        title: t`Ingredients`,
        url: Routes.MenuInventoryManager,
        isBeta: true,
        searchKeywords: ["inventory", "inventory manager", "ingredients"],
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.MultiMenus]: {
        title: t`Third Party`,
        url: Routes.MultiMenus,
        isBeta: true,
        searchKeywords: [
            "new menu",
            "additional menus",
            "third party",
            "deliverect",
        ],
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.MultiMenusCreate]: {
        title: t`Create Multi Menu`,
        url: Routes.MultiMenusCreate,
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.MultiMenusOutline]: {
        title: t`Multi Menu Outline`,
        url: Routes.MultiMenusOutline,
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.MultiMenusEdit]: {
        title: t`Edit Multi Menu`,
        url: Routes.MultiMenusEdit,
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.MultiMenusProductOverridesEdit]: {
        title: t`Edit Product Overrides`,
        url: Routes.MultiMenusProductOverridesEdit,
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.MultiMenusAddonOverridesEdit]: {
        title: t`Edit Addon Overrides`,
        url: Routes.MultiMenusAddonOverridesEdit,
        permission: StorePermission.HasMenuEditing,
    },
    [Routes.MultiMenusSettings]: {
        title: t`Multi Menu Settings`,
        url: Routes.MultiMenusSettings,
        permission: StorePermission.HasMenuEditing,
    },

    [Routes.GuestbookConversations]: {
        title: t`Conversations`,
        url: Routes.GuestbookConversations,
        searchKeywords: ["conversations", "message", "guestbook", "chat"],
        permission: StorePermission.HasGuestbook,
        icon: MessagesSquare,
    },
    [Routes.GuestbookCustomers]: {
        title: t`Guestbook`,
        url: Routes.GuestbookCustomers,
        searchKeywords: ["customers", "loyalty", "guestbook"],
        permission: StorePermission.HasGuestbook,
        icon: BookMarked,
    },
    [Routes.GuestbookAuditLog]: {
        title: t`Audit Log`,
        url: Routes.GuestbookAuditLog,
        searchKeywords: ["customers", "guestbook", "audit log"],
        permission: StorePermission.HasGuestbook,
        icon: History,
    },
    [Routes.GuestbookCampaignsSMS]: {
        title: t`SMS Campaigns`,
        url: Routes.GuestbookCampaignsSMS,
        searchKeywords: ["campaigns", "sms", "new campaign", "guestbook"],
        permission: StorePermission.HasGuestbook,
        icon: Megaphone,
    },
    [Routes.GuestbookCampaignsSMSDetails]: {
        title: t`SMS Campaign Details`,
        url: Routes.GuestbookCampaignsSMSDetails,
        permission: StorePermission.HasGuestbook,
    },

    // Growth - Marketing
    [Routes.Marketing]: {
        url: Routes.Marketing,
        title: t`Marketing`,
        icon: Rocket,
        searchKeywords: ["marketing", "campaigns", "promotions"],
        isNavigationOnly: true,
        permission: StorePermission.HasDashboard,
    },
    [Routes.Promotion]: {
        title: t`Promotions`,
        url: Routes.Promotion,
        searchKeywords: [
            "bogo",
            "promotions",
            "promos",
            "buy one get one",
            "happy hour",
            "promo code",
            "first-time customer",
            "deal drop",
            "student",
            "gift cards",
            "punchcard",
            "referral",
            "group buy",
            "party",
            "current promos",
            "discounts",
        ],
        permission: StorePermission.HasPromos,
        icon: BadgePercent,
        nonSidebarSubpages: [
            Routes.CreatePromotionGeneric,
            Routes.CreatePromotionReward,
            Routes.CreatePromotionGiftCard,
        ],
    },
    [Routes.CreatePromotionAudience]: {
        title: t`Audience`,
        url: Routes.CreatePromotionAudience,
        permission: StorePermission.HasPromos,
    },
    [Routes.CreatePromotionGeneric]: {
        title: t`Generic`,
        url: Routes.CreatePromotionGeneric,
        permission: StorePermission.HasPromos,
    },
    [Routes.CreatePromotionReward]: {
        title: t`Reward`,
        url: Routes.CreatePromotionReward,
        permission: StorePermission.HasPromos,
    },
    [Routes.CreatePromotionHappyHour]: {
        title: t`Happy Hour`,
        url: Routes.CreatePromotionHappyHour,
        permission: StorePermission.HasPromos,
    },
    [Routes.CreatePromotionBogo]: {
        title: t`Buy One Get One`,
        url: Routes.CreatePromotionBogo,
        permission: StorePermission.HasPromos,
    },
    [Routes.CreatePromotionStudents]: {
        title: t`Students`,
        url: Routes.CreatePromotionStudents,
        permission: StorePermission.HasPromos,
    },
    [Routes.CreatePromotionFirstTime]: {
        title: t`First Time`,
        url: Routes.CreatePromotionFirstTime,
        permission: StorePermission.HasPromos,
    },
    [Routes.CreatePromotionDealDrop]: {
        title: t`Deal Drop`,
        url: Routes.CreatePromotionDealDrop,
        permission: StorePermission.HasPromos,
    },
    [Routes.CreatePromotionPromoCode]: {
        title: t`Promo Code`,
        url: Routes.CreatePromotionPromoCode,
        permission: StorePermission.HasPromos,
    },
    [Routes.CreatePromotionGiftCard]: {
        title: t`Gift Card`,
        url: Routes.CreatePromotionGiftCard,
        permission: StorePermission.HasPromos,
    },
    [Routes.GiftCards]: {
        title: t`Gift Cards`,
        url: Routes.GiftCards,
        searchKeywords: ["gift card", "digital gift card", "sell gift card"],
        permission: StorePermission.HasPromos,
        icon: Gift,
    },
    [Routes.SettingsGiftCard]: {
        title: t`Gift Card Settings`,
        url: Routes.SettingsGiftCard,
        searchKeywords: ["gift card", "digital gift card", "sell gift card"],
        permission: StorePermission.HasPromos,
        icon: Settings2,
    },
    [Routes.ImportGiftCards]: {
        title: t`Import Gift Cards`,
        url: Routes.ImportGiftCards,
        searchKeywords: ["gift card", "import gift card"],
        permission: StorePermission.HasPromos,
        icon: Import,
    },

    [Routes.DigitalPresenceOnlineOrdering]: {
        title: t`Online Ordering`,
        url: Routes.DigitalPresenceOnlineOrdering,
        searchKeywords: ["online ordering", "online menu", "online presence"],
        permission: StorePermission.HasDashboard,
        externalUrl: (activeStore, _user) =>
            `https://order.snackpass.co/${activeStore?.slug || ""}`,
        icon: ShoppingBag,
    },
    [Routes.SettingsQrCode]: {
        title: t`QR Codes`,
        url: Routes.SettingsQrCode,
        searchKeywords: ["qr codes", "qr", "online ordering", "dine-in qr"],
        permission: StorePermission.HasDashboard,
        icon: QrCode,
    },
    [Routes.DigitalPresenceWebsite]: {
        title: t`Website`,
        url: Routes.DigitalPresenceWebsite,
        searchKeywords: ["website", "online presence", "website builder"],
        permission: StorePermission.HasDashboard,
        isBeta: true,
        externalUrl: (activeStore, user) =>
            getWebsiteSetupUrl({ store: activeStore, user }),
        icon: Brush,
    },
    [Routes.SettingsCatering]: {
        title: t`Catering`,
        url: Routes.SettingsCatering,
        searchKeywords: ["catering", "catering menu", "catering orders"],
        permission: StorePermission.HasDashboard,
        icon: UtensilsCrossed,
    },
    [Routes.BuyHardware]: {
        title: t`Buy Hardware`,
        url: Routes.BuyHardware,
        searchKeywords: ["hardware", "buy hardware", "equipment"],
        permission: StorePermission.HasDashboard,
        externalUrl: "https://store.snackpass.co/",
        icon: BaggageClaim,
    },
    [Routes.Invoices]: {
        title: t`Invoices`,
        url: Routes.Invoices,
        searchKeywords: ["invoices", "invoice", "billing"],
        permission: StorePermission.HasDashboard,
        icon: FileText,
    },
    [Routes.SettingsIntegrations]: {
        title: t`Integrations`,
        url: Routes.SettingsIntegrations,
        permission: StorePermission.HasSettings,
        icon: Puzzle,
    },

    // Data - Financial Reports
    [Routes.FinancialReports]: {
        title: t`More`,
        url: Routes.FinancialReports,
        icon: MoreHorizontal,
        permission: StorePermission.HasSalesReports,
        isNavigationOnly: true,
    },
    [Routes.FinancialReportsPayoutHistory]: {
        title: t`Payout History`,
        url: Routes.FinancialReportsPayoutHistory,
        permission: StorePermission.HasFinancialReports,
        searchKeywords: ["payouts", "payout history", "deposits"],
    },
    [Routes.FinancialReportsReconciliation]: {
        title: t`Reconciliation`,
        url: Routes.FinancialReportsReconciliation,
        permission: StorePermission.HasFinancialReports,
        searchKeywords: ["reconciliation", "payout calculation"],
    },
    [Routes.FinancialReportsCash]: {
        title: t`Cash`,
        url: Routes.FinancialReportsCash,
        permission: StorePermission.HasFinancialReports,
        icon: Coins,
    },
    [Routes.FinancialReportsEssentials]: {
        title: t`Reports`,
        url: Routes.FinancialReportsEssentials,
        permission: StorePermission.HasFinancialReports,
        searchKeywords: [
            "reports",
            "reports overview",
            "sales",
            "tips",
            "net sales",
            "gross sales",
        ],
    },
    [Routes.SalesReports]: {
        title: t`Sales`,
        url: Routes.SalesReports,
        permission: StorePermission.HasSalesReports,
        isNavigationOnly: true,
        icon: PiggyBank,
    },
    [Routes.Reports]: {
        title: t`More Reports`,
        url: Routes.Reports,
        permission: StorePermission.HasDashboard,
        icon: BarChart3,
        isNavigationOnly: true,
    },
    [Routes.ReportsSales]: {
        title: t`Sales`,
        url: Routes.ReportsSales,
        permission: StorePermission.HasSalesReports,
        isNavigationOnly: true,
    },
    [Routes.ReportsSalesSummary]: {
        title: t`Period Comparisons`,
        url: Routes.ReportsSalesSummary,
        searchKeywords: ["sales graphs"],
        permission: StorePermission.HasSalesReports,
    },
    [Routes.ReportsSalesChannels]: {
        title: t`Third Parties`,
        url: Routes.ReportsSalesChannels,
        searchKeywords: ["sales providers", "sales channels", "sales report"],
        permission: StorePermission.HasSalesReports,
        icon: Truck,
    },
    [Routes.ReportsMenu]: {
        title: t`Menu`,
        url: Routes.ReportsMenu,
        permission: StorePermission.HasReportsMenu,
        isNavigationOnly: true,
        icon: ShoppingBasket,
    },
    [Routes.ReportsMenuItemInsights]: {
        title: t`Items`,
        url: Routes.ReportsMenuItemInsights,
        searchKeywords: ["menu items", "menu insights", "menu statistics"],
        permission: StorePermission.HasReportsMenu,
    },
    [Routes.ReportsMenuCategoryInsights]: {
        title: t`Categories`,
        url: Routes.ReportsMenuCategoryInsights,
        searchKeywords: ["menu categories", "menu insights", "menu statistics"],
        permission: StorePermission.HasReportsMenu,
    },
    [Routes.ReportsCustomerDirectoryInsights]: {
        title: t`Customers`,
        url: Routes.ReportsCustomerDirectoryInsights,
        searchKeywords: [
            "customers",
            "customer directory",
            "customer insights",
        ],
        permission: StorePermission.HasReportsCustomers,
        icon: BookUser,
    },
    [Routes.ReportsPromotions]: {
        title: t`Promotions`,
        url: Routes.ReportsPromotions,
        searchKeywords: [
            "promotions",
            "promo codes",
            "promo campaigns",
            "promo discounts",
        ],
        permission: StorePermission.HasReportsPromotions,
        icon: SquarePercent,
    },
    [Routes.ReportsGiftCard]: {
        title: t`Gift Cards`,
        url: Routes.ReportsGiftCard,
        permission: StorePermission.HasReportsGiftCards,
        icon: Gift,
    },
    [Routes.ReportsGiftCardPurchases]: {
        title: t`Overview`,
        url: Routes.ReportsGiftCardPurchases,
        searchKeywords: [
            "gift card overview",
            "gift card purchases",
            "gift card transactions",
        ],
        permission: StorePermission.HasReportsGiftCards,
    },
    [Routes.ReportsGiftCardBalances]: {
        title: t`Balances`,
        url: Routes.ReportsGiftCardBalances,
        searchKeywords: [
            "gift card balances",
            "gift card transactions",
            "gift card reports",
        ],
        permission: StorePermission.HasReportsGiftCards,
    },
    [Routes.ReportsGiftCardTransactions]: {
        title: t`Transactions`,
        url: Routes.ReportsGiftCardTransactions,
        permission: StorePermission.HasReportsGiftCards,
    },
    [Routes.ReportsGiftCardLiabilities]: {
        title: t`Liabilities`,
        url: Routes.ReportsGiftCardLiabilities,
        permission: StorePermission.HasReportsGiftCards,
    },
    [Routes.ReportsLocation]: {
        title: t`Locations`,
        url: Routes.ReportsLocation,
        permission: StorePermission.HasReportsLocations,
        isNavigationOnly: true,
        icon: Map,
    },
    [Routes.ReportsLocationSales]: {
        title: t`Sales`,
        url: Routes.ReportsLocationSales,
        permission: StorePermission.HasReportsLocations,
    },
    [Routes.ReportsLocationMenuCategories]: {
        title: t`Categories`,
        url: Routes.ReportsLocationMenuCategories,
        permission: StorePermission.HasReportsLocations,
    },
    [Routes.ReportsLocationMenuItems]: {
        title: t`Items`,
        url: Routes.ReportsLocationMenuItems,
        permission: StorePermission.HasReportsLocations,
    },

    // Data - Labor
    [Routes.Labor]: {
        title: t`Labor`,
        url: Routes.Labor,
        icon: Users2,
        permission: StorePermission.HasReportsLabor,
        isNavigationOnly: true,
    },

    // Data - Time Cards
    [Routes.EmployeeTimeCards]: {
        title: t`Time Cards`,
        url: Routes.EmployeeTimeCards,
        icon: Clock,
        permission: StorePermission.HasEmployees,
    },
    [Routes.Tipping]: {
        title: t`Tips`,
        url: Routes.Tipping,
        permission: StorePermission.HasReportsLabor,
        icon: HandHeart,
        searchKeywords: ["tips", "tipping", "tips report", "labor report"],
    },
    [Routes.OrderAudit]: {
        title: t`Order Audit`,
        url: Routes.OrderAudit,
        permission: StorePermission.HasReportsLabor,
        icon: Users2,
        searchKeywords: [
            "order audit",
            "employee reporting",
            "labor audit",
            "tips",
            "voids",
            "refunds",
            "discounts",
            "audit",
            "audit report",
            "custom discounts",
        ],
    },
    [Routes.MinuteToMinute]: {
        title: t`Hourly`,
        url: Routes.MinuteToMinute,
        permission: StorePermission.HasSalesReports,
        searchKeywords: [
            "hourly",
            "tips hourly",
            "sales hourly",
            "minute to minute",
            "minute to minute reports",
            "hourly",
        ],
    },

    // Settings - Store
    [Routes.Settings]: {
        title: t`Settings`,
        url: Routes.Settings,
        permission: StorePermission.HasSettings,
    },
    [Routes.SettingsBusinessInfo]: {
        title: t`Store`,
        url: Routes.SettingsBusinessInfo,
        icon: Store,
        searchKeywords: [
            "business info",
            "manage information",
            "display about store",
            "store display",
            "store id",
            "copy store id",
            "store location",
            "store name",
            "store appearance",
            "customers",
            "store address",
            "ordering page",
            "app page",
            "store timezone",
            "store type",
            "contact information",
            "store phone number",
            "phone number",
            "store email address",
            "account information",
            "subscriptions",
            "tax",
            "schedule ahead orders",
            "store hours",
            "time zone",
            "store schedule",
            "special hours",
            "override store hours",
            "cancel scheduled orders",
            "third party delivery hours",
            "holiday hours",
        ],
        permission: StorePermission.HasSettings,
    },

    [Routes.SettingsPermissions]: {
        title: t`Team`,
        url: Routes.SettingsPermissions,
        searchKeywords: ["users", "permissions", "team management"],
        permission: StorePermission.HasEmployees,
        icon: Users,
    },
    [Routes.SettingsEmployees]: {
        title: t`Clock In and Out`,
        url: Routes.SettingsEmployees,
        searchKeywords: ["clock in", "clock out", "employee time cards"],
        permission: StorePermission.HasSettings,
    },

    // Settings - Devices
    [Routes.Devices]: {
        title: t`Devices`,
        url: Routes.Devices,
        icon: Monitor,
        permission: StorePermission.HasDevices,
    },

    // Settings - Front of House
    [Routes.SettingsFrontOfHouse]: {
        title: t`Front of House`,
        url: Routes.SettingsFrontOfHouse,
        icon: Utensils,
        permission: StorePermission.HasSettings,
        isNavigationOnly: true,
    },
    [Routes.SettingsBrandAndAppearance]: {
        title: t`Brand and Appearance`,
        url: Routes.SettingsBrandAndAppearance,
        permission: StorePermission.HasSettings,
    },
    [Routes.SettingsLookbookBuilder]: {
        title: t`Lookbook`,
        url: Routes.SettingsLookbookBuilder,
        searchKeywords: [
            "lookbook",
            "photo gallery",
            "menu photos",
            "store photos",
            "image gallery",
        ],
        permission: StorePermission.HasSettings,
    },
    [Routes.SettingsTipping]: {
        title: t`Tipping`,
        url: Routes.SettingsTipping,
        permission: StorePermission.HasSettings,
    },
    [Routes.SettingsAllOrders]: {
        title: t`All Orders`,
        url: Routes.SettingsAllOrders,
        permission: StorePermission.HasSettings,
    },
    [Routes.SettingsOnlineOrders]: {
        title: t`Online Orders`,
        url: Routes.SettingsOnlineOrders,
        searchKeywords: ["utensils", "cutlery", "napkins", "skip the stuff"],
        permission: StorePermission.HasSettings,
    },
    [Routes.SettingsPlatforms]: {
        title: t`Kiosk and Register`,
        url: Routes.SettingsPlatforms,
        searchKeywords: ["kiosk", "register", "point of sale", "POS"],
        permission: StorePermission.HasSettings,
    },

    // Settings - Back of House
    [Routes.SettingsBOH]: {
        title: t`Back of House`,
        url: Routes.SettingsBOH,
        icon: ChefHat,
        permission: StorePermission.HasSettings,
        isNavigationOnly: true,
    },
    [Routes.SettingsBackOfHouse]: {
        title: t`Order Flow`,
        url: Routes.SettingsBackOfHouse,
        searchKeywords: ["order flow", "order management", "order tracking"],
        permission: StorePermission.HasSettings,
    },
    [Routes.PrepStations]: {
        title: t`Prep Stations`,
        url: Routes.PrepStations,
        searchKeywords: [
            "prep stations",
            "kitchen equipment",
            "kitchen layout",
        ],
        permission: StorePermission.HasDevices,
    },
    [Routes.SettingsStoreTablesLayout]: {
        title: t`Table Editor`,
        url: Routes.SettingsStoreTablesLayout,
        searchKeywords: ["table editor", "store layout", "table management"],
        permission: StorePermission.HasSettings,
    },
    [Routes.Machines]: {
        title: t`Machines`,
        url: Routes.Machines,
        searchKeywords: ["machine"],
        permission: StorePermission.HasDevices,
    },

    // Settings - Account and Tax
    [Routes.AccountAndTax]: {
        title: t`Account & Tax`,
        url: Routes.AccountAndTax,
        icon: Landmark,
        permission: StorePermission.HasPayouts,
        isNavigationOnly: true,
    },
    [Routes.Billing]: {
        title: t`Billing`,
        url: Routes.Billing,
        permission: StorePermission.HasBilling,
    },
    [Routes.Payouts]: {
        title: t`Bank Account`,
        url: Routes.Payouts,
        searchKeywords: ["account", "financial", "payouts", "bank"],
        permission: StorePermission.HasPayouts,
    },
    [Routes.PayoutsSchedule]: {
        title: t`Payout Schedule`,
        url: Routes.PayoutsSchedule,
        searchKeywords: [
            "payout schedule",
            "payout periods",
            "payout management",
        ],
        permission: StorePermission.HasPayouts,
    },
    [Routes.Contract]: {
        title: t`Contract`,
        url: Routes.Contract,
        permission: StorePermission.IsOwner,
    },
    [Routes.TaxForms]: {
        title: t`Tax Forms`,
        url: Routes.TaxForms,
        searchKeywords: ["tax forms", "tax returns", "tax preparation"],
        permission: StorePermission.HasSettings,
    },
    [Routes.SettingsTax]: {
        title: t`Tax Rates`,
        url: Routes.SettingsTax,
        searchKeywords: ["tax rates", "tax calculations", "tax management"],
        permission: StorePermission.HasSettings,
    },

    // Settings - Other
    [Routes.Other]: {
        title: t`Other`,
        url: Routes.Other,
        icon: Settings2,
        permission: StorePermission.HasSettings,
        isNavigationOnly: true,
    },
    [Routes.SettingsProducts]: {
        title: t`Products`,
        url: Routes.SettingsProducts,
        searchKeywords: [
            "products",
            "product management",
            "inventory management",
        ],
        permission: StorePermission.HasSettings,
    },
    [Routes.SettingsOrders]: {
        title: t`Orders`,
        url: Routes.SettingsOrders,
        permission: StorePermission.HasSettings,
    },
    [Routes.SettingsLegal]: {
        title: t`Legal`,
        url: Routes.SettingsLegal,
        permission: StorePermission.HasSettings,
    },
    [Routes.SettingsCustomerFees]: {
        title: t`Customer Fees`,
        url: Routes.SettingsCustomerFees,
        searchKeywords: ["customer fees", "service fee", "bag fee"],
        permission: StorePermission.HasSettings,
    },

    // Settings - Snackpass Admin
    [Routes.SnackpassAdmin]: {
        title: t`Snackpass`,
        url: Routes.SnackpassAdmin,
        icon: Lock,
    },
    [Routes.InternalSettings]: {
        title: t`General`,
        url: Routes.InternalSettings,
        searchKeywords: ["general", "settings", "internal settings"],
    },
    [Routes.InternalFees]: {
        title: t`Fee Settings`,
        url: Routes.InternalFees,
        searchKeywords: ["fees", "fee settings", "internal fees"],
    },
    [Routes.InternalSeniorSupport]: {
        title: t`Senior Support`,
        url: Routes.InternalSeniorSupport,
        searchKeywords: ["senior support", "internal senior support"],
    },
    [Routes.InternalDevices]: {
        title: t`Search Devices`,
        url: Routes.InternalDevices,
        searchKeywords: ["devices", "internal devices"],
    },

    // Others
    [Routes.MyAccountTeams]: {
        title: t`Teams`,
        url: Routes.MyAccountTeams,
    },
    [Routes.SettingsAccount]: {
        title: t`Account`,
        url: Routes.SettingsAccount,
    },
    [Routes.NewUserAcceptInvite]: {
        title: t`Accept Invite`,
        url: Routes.NewUserAcceptInvite,
    },
    [Routes.NotAllowed]: {
        title: t`Not Allowed`,
        url: Routes.NotAllowed,
    },
    [Routes.NotFound]: {
        title: t`Not Found`,
        url: Routes.NotFound,
    },
    [Routes.Logout]: {
        title: t`Logout`,
        url: Routes.Logout,
    },
});
